import { ChangeEvent, useRef, useState } from "react";
import lodash from "lodash";
import { NestedKeys } from "advanced-types";
import { NestedType } from "../typings/nestedType";
import { useImmer } from "use-immer";
import { deepClone } from "../utils/functions";

export const useSimpleForm = <T extends object, K extends Boolean = false>(
  initialValue?: T,
  nullable?: K,
  useImmerState?: boolean
) => {
  const [form, setForm] = (useImmerState ? useImmer : useState)<T>(
    initialValue as any
  );
  const initialValueForm = useRef(JSON.parse(JSON.stringify(initialValue)));
  const handleChange = <K extends NestedKeys<T, ".", 3>>(
    key: K,
    value: NestedType<T, K>
  ) => {
    setForm((prev) => {
      const clone = deepClone(prev);
      prev && lodash.set(clone, key, value);
      return clone;
    });
  };
  const handleRemoveKey = <K extends NestedKeys<T, ".", 3>>(key: K) => {
    setForm((prev) => {
      const clone = deepClone(prev);
      clone && key in clone && delete (clone as any)[key];
      return clone;
    });
  };
  const handleEventInputChange = <K extends NestedKeys<T, ".", 3>>(
    key: K,
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setForm((prev) => {
      const clone = deepClone(prev);
      clone && lodash.set(clone, key, event.target.value);
      return clone;
    });
  };
  const handleEventInputChange2 =
    <K extends NestedKeys<T, ".", 3>>(key: K) =>
    (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setForm((prev) => {
        const clone = deepClone(prev);
        clone && lodash.set(clone, key, event.target.value);
        return clone;
      });
    };
  const onInputChange =
    <K extends NestedKeys<T, ".", 3>>(key: K) =>
    (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setForm((prev) => {
        const clone = deepClone(prev);
        clone && lodash.set(clone, key, event.target.value);
        return clone;
      });
    };

  const handleCopyObjectForm = (object: any) => {
    setForm((prev) => {
      const clone = deepClone(prev);
      clone &&
        Object.entries(object).forEach(([key, value]) => {
          if (key in clone) lodash.set(clone, key, value);
        });
      return clone;
    });
  };
  const resetForm = () => {
    setForm(JSON.parse(JSON.stringify(initialValueForm.current)));
  };

  // const updateForm = (object: any) => {
  //   setForm((prev) => {
  //     prev = object;
  //   });
  // };

  return {
    form: form as K extends true ? T | undefined : T,
    setForm,
    handleChange,
    onInputChange,
    handleEventInputChange,
    resetForm,
    handleEventInputChange2,
    handleRemoveKey,
    handleCopyObjectForm,
  };
};

export type ISimpleForm<
  T extends object,
  K extends Boolean = false
> = typeof useSimpleForm<T, K>;
